/**
 * @fileoverview Pure selection helpers for the legacy migration grid.
 * @module system-web/services/legacyMigrationSelection
 *
 * Responsibilities:
 * - Distinguish current-page selection from full-dataset selection.
 * - Build the startRun selection payload without turning a page select into ['*'].
 *
 * Dependencies:
 * - None. Pure functions consumed by LegacyMigrationGrid and LegacyMigrationPage.
 *
 * Architecture:
 * - Grid pages are server-paginated, so the page checkbox can only know visible IDs.
 * - Full-dataset select is an explicit second checkbox. The header checkbox never counts unseen rows.
 *
 * Notes:
 * - Client-only filters (column filters, query builder, sub-category) shrink the visible
 *   page without changing server total, so full-dataset select must stay hidden then.
 */

/**
 * @contract MigrationGridSelection
 * Purpose: In-memory selection state for the migration grid.
 * Fields: selectionType, selectedIds, excludedIds
 * Rules: selectionType 'all' means every server-filtered row except excludedIds
 * Sync: local UI state only — submitted as startRun.selection
 */
export type MigrationSelectionType = 'explicit' | 'all';

/**
 * @process togglePageIds
 * Steps: add or remove the visible selectable page IDs from an explicit set
 * Inputs: current selected IDs, page IDs, whether the page is fully selected
 * Outputs: next explicit ID set
 * Failure Handling: empty page IDs is a no-op
 * Idempotency: REQUIRED
 */
export function togglePageIds(
  selectedIds: Iterable<string>,
  pageIds: string[],
  pageFullySelected: boolean,
): Set<string> {
  const next = new Set(selectedIds);
  if (pageFullySelected) {
    for (const id of pageIds) next.delete(id);
  } else {
    for (const id of pageIds) next.add(id);
  }
  return next;
}

/**
 * @process shouldOfferDatasetSelect
 * Steps: offer the second checkbox only when the page is fully selected and more
 *   server-filtered rows exist beyond this page, with no client-only narrowing
 * Inputs: page selected flag, visible selectable count, server total, client-only filter flag
 * Outputs: whether to render "Select all N records"
 * Failure Handling: missing total hides the extra checkbox
 * Idempotency: REQUIRED
 */
export function shouldOfferDatasetSelect(input: {
  pageFullySelected: boolean;
  visibleSelectableCount: number;
  serverTotal: number;
  hasClientOnlyFilters: boolean;
}): boolean {
  return (
    input.pageFullySelected &&
    !input.hasClientOnlyFilters &&
    input.serverTotal > input.visibleSelectableCount &&
    input.serverTotal > 0
  );
}

/**
 * @process resolveStartRunSelection
 * Steps: preserve explicit IDs or encode wildcard exclusions plus source/slot filters.
 * Inputs: selection type, IDs, exclusions, optional filters, and forceAll.
 * Outputs: runner-compatible selection token array.
 * Failure Handling: empty explicit selection returns []
 * Idempotency: REQUIRED
 */
export function resolveStartRunSelection(input: {
  selectionType: MigrationSelectionType;
  selectedIds: Iterable<string>;
  excludedIds?: Iterable<string>;
  sourceTables?: Iterable<string>;
  slots?: Iterable<string>;
  band?: string;
  searchQuery?: string;
  columnFilters?: Record<string, string>;
  queryJson?: string;
  forceAll?: boolean;
}): string[] {
  const searchFilter = input.searchQuery?.trim() ? [`@search:${input.searchQuery.trim()}`] : [];
  const colFilters = Object.entries(input.columnFilters ?? {})
    .filter(([k, v]) => k !== 'sourceTable' && k !== 'parentCategory' && v && v.trim())
    .map(([k, v]) => `@filter:${encodeURIComponent(k)}=${encodeURIComponent(v.trim())}`);
  const queryFilter = input.queryJson?.trim() ? [`@query:${input.queryJson.trim()}`] : [];

  const filters = [
    ...Array.from(input.sourceTables ?? [], (value) => `@source:${value}`),
    ...Array.from(input.slots ?? [], (value) => `@slot:${value}`),
    ...(input.band && input.band !== 'all' ? [`@band:${input.band}`] : []),
    ...searchFilter,
    ...colFilters,
    ...queryFilter,
  ];
  if (input.forceAll || input.selectionType === 'all') {
    return ['*', ...Array.from(input.excludedIds ?? [], (id) => `!${id}`), ...filters];
  }
  return [...Array.from(input.selectedIds), ...filters];
}

/**
 * @process effectiveSelectionCount
 * Steps: count explicit IDs, or server total minus exclusions for dataset select
 * Inputs: selection type, explicit size, excluded size, server total
 * Outputs: number shown on Dry Run / Migrate
 * Failure Handling: never returns below 0
 * Idempotency: REQUIRED
 */
export function effectiveSelectionCount(input: {
  selectionType: MigrationSelectionType;
  selectedCount: number;
  excludedCount: number;
  serverTotal: number;
}): number {
  if (input.selectionType === 'all') {
    return Math.max(0, input.serverTotal - input.excludedCount);
  }
  return input.selectedCount;
}

/**
 * @process visibleSelectionCount
 * Steps: count only IDs that belong to the rows currently shown for this dataset
 * Inputs: selected IDs and the active tab's loaded row IDs
 * Outputs: badge count for Dry Run / Migrate
 * Failure Handling: no visible IDs means the previous tab's selection does not count
 * Idempotency: REQUIRED
 */
export function visibleSelectionCount(selectedIds: Iterable<string>, visibleIds: Iterable<string>): number {
  const visible = new Set(visibleIds);
  let count = 0;
  for (const id of selectedIds) {
    if (visible.has(id)) count += 1;
  }
  return count;
}
